import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const OrganizationProfile = () => {
  const [formData, setFormData] = useState({
    organizationName: '',
    legalName: '',
    industry: '',
    businessType: '',
    taxId: '',
    registrationNumber: '',
    website: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    description: ''
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('/assets/images/no_image.png');
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });
  const [isModified, setIsModified] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setIsModified(true);
    // Clear save status when user makes changes
    if (saveStatus?.message) {
      setSaveStatus({ type: '', message: '' });
    }
  };

  const handleLogoChange = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      // Validate file type and size
      if (!file?.type?.startsWith('image/')) {
        setSaveStatus({ 
          type: 'error', 
          message: 'Please select a valid image file.' 
        });
        return;
      }
      
      if (file?.size > 2 * 1024 * 1024) { // 2MB limit
        setSaveStatus({ 
          type: 'error', 
          message: 'File size must be less than 2MB.' 
        });
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader?.result);
        setIsModified(true);
        setSaveStatus({ type: '', message: '' });
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview('/assets/images/no_image.png');
    setLogoFile(null);
    setIsModified(true);
    // Reset the file input
    const fileInput = document?.getElementById('logo-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const validateForm = () => {
    const errors = [];
    
    if (!formData?.organizationName?.trim()) {
      errors?.push('Organization name is required');
    }
    
    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      errors?.push('Please enter a valid email address');
    }
    
    if (formData?.website && !/^https?:\/\/.+/?.test(formData?.website)) {
      errors?.push('Please enter a valid website URL (include http:// or https://)');
    }

    return errors;
  };

  const handleSave = async () => {
    const validationErrors = validateForm();
    
    if (validationErrors?.length > 0) {
      setSaveStatus({ 
        type: 'error', 
        message: validationErrors?.join(', ') 
      });
      return;
    }

    setIsLoading(true);
    setSaveStatus({ type: '', message: '' });

    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log('Saving organization profile:', { formData, logoFile });
      
      setSaveStatus({ 
        type: 'success', 
        message: 'Organization profile updated successfully!' 
      });
      setIsModified(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveStatus({ type: '', message: '' });
      }, 3000);
      
    } catch (error) {
      setSaveStatus({ 
        type: 'error', 
        message: 'Failed to save changes. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (isModified) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to cancel?');
      if (!confirmed) return;
    }
    
    // TODO: Reset to original values or refetch from API
    setFormData({
      organizationName: '',
      legalName: '',
      industry: '',
      businessType: '',
      taxId: '',
      registrationNumber: '',
      website: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      description: ''
    });
    setLogoPreview('/assets/images/no_image.png');
    setLogoFile(null);
    setIsModified(false);
    setSaveStatus({ type: '', message: '' });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Organization Profile</h2>
          <p className="text-muted-foreground">Manage your organization's basic information and branding</p>
        </div>
        <div className="flex items-center space-x-3">
          {isModified && (
            <span className="text-sm text-yellow-600 dark:text-yellow-400">
              You have unsaved changes
            </span>
          )}
          <Button 
            onClick={handleSave} 
            iconName="Save"
            disabled={isLoading || !isModified}
            className={isLoading ? 'opacity-50' : ''}
          >
            {isLoading ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
      {/* Status Messages */}
      {saveStatus?.message && (
        <div className={`p-4 rounded-lg border ${
          saveStatus?.type === 'success' ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400' : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
        }`}>
          <div className="flex items-center space-x-2">
            <Icon 
              name={saveStatus?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} 
              size={16} 
            />
            <span className="text-sm font-medium">{saveStatus?.message}</span>
          </div>
        </div>
      )}
      {/* Organization Logo */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">Organization Logo</h3>
        <div className="flex items-center space-x-6">
          <div className="relative group">
            <img
              src={logoPreview}
              alt="Organization Logo"
              className="w-24 h-24 rounded-lg object-cover border border-border"
            />
            <div 
              className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 rounded-lg flex items-center justify-center transition-opacity cursor-pointer"
              onClick={() => document?.getElementById('logo-upload')?.click()}
            >
              <Icon name="Camera" size={24} color="white" />
            </div>
          </div>
          <div className="flex-1">
            <p className="text-sm text-muted-foreground mb-3">
              Upload your organization logo. Recommended size: 200x200px, max 2MB.
              Supported formats: JPG, PNG, GIF, SVG.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => document?.getElementById('logo-upload')?.click()}
                iconName="Upload"
              >
                Upload Logo
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemoveLogo}
                iconName="Trash2"
                disabled={logoPreview === '/assets/images/no_image.png'}
              >
                Remove
              </Button>
            </div>
            <input
              id="logo-upload"
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="hidden"
            />
          </div>
        </div>
      </div>
      {/* Basic Information */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Organization Name"
            value={formData?.organizationName}
            onChange={(e) => handleInputChange('organizationName', e?.target?.value)}
            required
            placeholder="Enter organization name"
          />
          <Input
            label="Legal Name"
            value={formData?.legalName}
            onChange={(e) => handleInputChange('legalName', e?.target?.value)}
            placeholder="Enter legal name"
          />
          <Input
            label="Industry"
            value={formData?.industry}
            onChange={(e) => handleInputChange('industry', e?.target?.value)}
            placeholder="Enter industry"
          />
          <Input
            label="Business Type"
            value={formData?.businessType}
            onChange={(e) => handleInputChange('businessType', e?.target?.value)}
            placeholder="Enter business type"
          />
          <Input
            label="Tax ID"
            value={formData?.taxId}
            onChange={(e) => handleInputChange('taxId', e?.target?.value)}
            placeholder="Enter tax identification number"
          />
          <Input
            label="Registration Number"
            value={formData?.registrationNumber}
            onChange={(e) => handleInputChange('registrationNumber', e?.target?.value)}
            placeholder="Enter registration number"
          />
        </div>
      </div>
      {/* Contact Information */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Contact Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Website"
            value={formData?.website}
            onChange={(e) => handleInputChange('website', e?.target?.value)}
            placeholder="https://example.com"
          />
          <Input
            label="Email"
            type="email"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            placeholder="contact@example.com"
          />
          <Input
            label="Phone"
            value={formData?.phone}
            onChange={(e) => handleInputChange('phone', e?.target?.value)}
            placeholder="+1 (555) 123-4567"
          />
        </div>
      </div>
      {/* Address Information */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Address Information</h3>
        <div className="space-y-6">
          <Input
            label="Street Address"
            value={formData?.address}
            onChange={(e) => handleInputChange('address', e?.target?.value)}
            placeholder="Enter street address"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Input
              label="City"
              value={formData?.city}
              onChange={(e) => handleInputChange('city', e?.target?.value)}
              placeholder="Enter city"
            />
            <Input
              label="State/Province"
              value={formData?.state}
              onChange={(e) => handleInputChange('state', e?.target?.value)}
              placeholder="Enter state"
            />
            <Input
              label="Postal Code"
              value={formData?.postalCode}
              onChange={(e) => handleInputChange('postalCode', e?.target?.value)}
              placeholder="Enter postal code"
            />
            <Input
              label="Country"
              value={formData?.country}
              onChange={(e) => handleInputChange('country', e?.target?.value)}
              placeholder="Enter country"
            />
          </div>
        </div>
      </div>
      {/* Description */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Organization Description</h3>
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Description
          </label>
          <textarea
            value={formData?.description}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
            placeholder="Enter organization description"
            rows={4}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex justify-end space-x-4 pt-6 border-t border-border">
        <Button 
          variant="outline" 
          onClick={handleCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          iconName="Save"
          disabled={isLoading || !isModified}
          className={isLoading ? 'opacity-50' : ''}
        >
          {isLoading ? (
            <>
              <Icon name="Loader2" size={16} className="animate-spin mr-2" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </div>
  );
};

export default OrganizationProfile;